// ABOUTME: Screen 4B Forgive, in two beats: the stormy card with Forgive, then the dissolved card with Learn and Let go.
// ABOUTME: forgive is a blocking stay; both beats are read from the crossing's repair status, so a reload draws the end state with no replay.

import { useEffect, useRef } from 'react';
import { RepairStatus } from '../../../types/api';
import type { ScreenProps, ScreenResult } from '../runner/screenContract';
import ArrivalStatus from './clouds/ArrivalStatus';
import ImpactSkyMask from './impact/ImpactSkyMask';
import { useStormDissolve } from './impact/stormDissolve';
import { RECEIVER_HEADLINE, RECEIVER_SUB } from './receiver/receiverCopy';

const FORGIVE_ACTION_ID = 'forgive';
const LEARN_ACTION_ID = 'learn_and_let_go';

// Moves keyboard focus to Learn and Let go when Forgive is pressed here. Arriving already forgiven leaves focus to the frame.
function FocusWhenOffered({ forgiven }: { forgiven: boolean }) {
  const arrivedForgiven = useRef(forgiven);
  useEffect(() => {
    if (!forgiven || arrivedForgiven.current) return undefined;
    const button = () => document.querySelector<HTMLElement>(`[data-action-id="${LEARN_ACTION_ID}"]`);
    button()?.focus();
    if (document.activeElement === button()) return undefined;
    // The plate draws the new button in the same update; if it is not in the DOM yet, try once more next frame.
    const frame = requestAnimationFrame(() => button()?.focus());
    return () => cancelAnimationFrame(frame);
  }, [forgiven]);
  return null;
}

export default function Screen4B({ crossing }: ScreenProps): ScreenResult {
  const forgiven = crossing.repairStatus === RepairStatus.CompleteForgiven;
  const { present, pressed } = useStormDissolve(forgiven);
  const label = crossing.progress.actions.find(action => action.id === FORGIVE_ACTION_ID || action.id === LEARN_ACTION_ID)?.label ?? '';
  return {
    // The headline is shared with 5B, so the button label is what the announcement adds, and it changes with the beat.
    layer: (
      <>
        {present && <ImpactSkyMask still lifted={forgiven} />}
        <FocusWhenOffered forgiven={forgiven} />
        <ArrivalStatus text={label} />
      </>
    ),
    headline: RECEIVER_HEADLINE,
    sub: RECEIVER_SUB,
    // The sky turns forgiven with the dissolve, fading in for a press played here and there at once on arrival.
    sceneSky: forgiven ? 'forgiven' : undefined,
    sceneSkyFadesIn: pressed,
  };
}
